import {FC, useState, useContext} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import {useMutation, useApolloClient} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {DISABLE_PALOWAN_VACATION_ASSIGNMENT} from 'graphql/palowan-vacation/Mutations';
import {alertContext} from 'contextApi/context/error';
import {PalowanVacationAssignment} from 'models/PalowanVacationAssignment';

interface PalowanVacationDisableModalProps {
  open: boolean;
  assignment: PalowanVacationAssignment | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Confirmation modal for disabling a palowan-level vacation assignment.
 * Prompts the admin to confirm the action before executing the
 * DISABLE_PALOWAN_VACATION_ASSIGNMENT mutation. On success, dispatches a
 * success Snackbar, refetches the assignments list, and calls onSuccess.
 * On failure, inspects the error message to dispatch a context-specific
 * error Snackbar.
 *
 * @param open - Whether the dialog is visible.
 * @param assignment - The vacation assignment to disable, or null if none is selected.
 * @param onClose - Callback invoked when the dialog should close without action.
 * @param onSuccess - Callback invoked after the assignment is successfully disabled.
 */
const PalowanVacationDisableModal: FC<PalowanVacationDisableModalProps> = ({
  open,
  assignment,
  onClose,
  onSuccess,
}) => {
  const {t} = useTranslation();
  const {dispatch} = useContext(alertContext);
  const client = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [disableVacationAssignment] = useMutation(
    DISABLE_PALOWAN_VACATION_ASSIGNMENT
  );

  /**
   * Executes the disable mutation and handles success and error outcomes.
   * Inspects the server error message to dispatch the most specific error
   * Snackbar: "already disabled", "not found", or a generic fallback.
   */
  const handleConfirm = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      await disableVacationAssignment({variables: {id: assignment!.id}});
      dispatch({
        type: 'show',
        payload: {
          message: t('leaves.PalowanVacationDisableModal.successMessage'),
          level: 'success',
          duration: 5000,
        },
      });
      await client.refetchQueries({include: ['getPalowanVacationAssignments']});
      onSuccess();
    } catch (err: unknown) {
      const apolloErr = err as {
        message?: string;
        graphQLErrors?: Array<{message: string}>;
      };
      const errorMessage =
        apolloErr?.graphQLErrors?.[0]?.message ?? apolloErr?.message ?? '';

      let i18nKey: string;
      if (errorMessage.includes('deshabilitado')) {
        i18nKey = 'leaves.PalowanVacationDisableModal.alreadyDisabledError';
      } else if (errorMessage.includes('encontró')) {
        i18nKey = 'leaves.PalowanVacationDisableModal.notFoundError';
      } else {
        i18nKey = 'leaves.PalowanVacationDisableModal.errorMessage';
      }

      dispatch({
        type: 'show',
        payload: {
          message: t(i18nKey),
          level: 'error',
          duration: 5000,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('leaves.PalowanVacationDisableModal.title')}</DialogTitle>
      <DialogContent>
        <Typography>
          {t('leaves.PalowanVacationDisableModal.message', {
            palowanName: `${assignment?.palowan.firstName} ${assignment?.palowan.lastName}`,
            from: assignment?.seniorityFromYears,
            to: assignment?.seniorityToYears,
          })}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('leaves.PalowanVacationDisableModal.cancelButton')}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!assignment || isSubmitting}
          color="error"
          variant="contained"
        >
          {t('leaves.PalowanVacationDisableModal.confirmButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PalowanVacationDisableModal;
